/* eslint-disable @typescript-eslint/ban-ts-comment */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import apiGateway from '../../../services/apiGateway'
import { WebcastStatusForVotes, Vote, VotesErrorType, VotesMode, VoteStatus } from '@bt-react/components/Votes'
import { State } from '../../reducers/reducerType'
import xorWith from 'lodash/xorWith'
import isEqual from 'lodash/isEqual'
import selectWebcastStatusForVotes from '../../selectors/selectWebcastStatusForVotes'

export interface VotesSlice {
  votes: Vote[],
  mode: VotesMode,
  selectedVoteId: number | undefined,
  error: VotesErrorType | undefined,
  webcastStatus: WebcastStatusForVotes,
  countdownStart?: number,
  pendingChanges: number[]
}

export const votesDefaultState: VotesSlice = {
  votes: [],
  mode: VotesMode.LIST,
  selectedVoteId: undefined,
  error: undefined,
  webcastStatus: WebcastStatusForVotes.UPCOMING,
  pendingChanges: []
}

export const serialiseVote = (vote:Vote, webcastId) => {

  return {
    ...vote,
    webcast: {
      "id": webcastId
    }
  }
}

export const createVote =
  createAsyncThunk
  ('CREATE_VOTE', async (vote:Vote, { getState }) => {

    // @ts-ignore
    const webcastId:string = getState().config.roomname.split('-')[1]
    const request = serialiseVote( vote, webcastId )

    const response = await apiGateway.createVote(request)
    return { ...vote, id: response.data.id, status: VoteStatus.notstarted }

  })

export const deleteVote =
  createAsyncThunk
  ('DELETE_VOTE', async (id: number) => {

    await apiGateway.deleteVote(id)
    return { id }

  })

export const editVote =
  createAsyncThunk
  ('EDIT_VOTE', async (vote:Vote) => {
    
    await apiGateway.updateVote({
      id: vote.id!,
      question: vote.question!,
      options: vote.options.map( option => ({ value: option.value }))
    })

    return vote

  })

export const startVote =
  createAsyncThunk
  ('START_VOTE', async (voteId:number, { getState }) => {

    // @ts-ignore
    const openVote = getState().votes.votes.find( (vote:Vote) => vote.status === VoteStatus.open)

    if( openVote && openVote.id !== voteId ){

      await apiGateway.updateVote(  {
        id: openVote.id,
        status: VoteStatus.closed
      })
    }

    await apiGateway.updateVote({
      id: voteId!,
      status: VoteStatus.open
    })

    return { voteId, openVoteId: openVote?.id }

  })

export const endVote =
  createAsyncThunk
  ('END_VOTE', async (id:number) => {

    await apiGateway.updateVote( {
      id: id!,
      status: VoteStatus.closed
    })

    return { id }

  })

// To close all votes when webcast completes. In reality is only be one.
export const endAllVotes =
  createAsyncThunk
  ('END_ALL_VOTES', async ( value, {getState}) => {

    // @ts-ignore
    const openVote: Vote = getState().votes.votes?.find( (vote:Vote) => vote.status === VoteStatus.open)

    if( openVote ){

      await apiGateway.updateVote( {
        id: openVote.id!,
        status: VoteStatus.closed
      })

      return { id: openVote.id! }

    }

  })

const votesSlice = createSlice({
  name: 'votes',
  initialState: votesDefaultState,
  reducers: {
    setMode( state, { payload } ) {
      state.selectedVoteId = payload.selectedVoteId
      state.mode = payload.mode
    },
    setVotes(state, { payload }) {

      if(payload.isLive) {

        if(typeof payload?.data?.list?.length === 'number') {

          const liveVote = payload.data.list.find( vote => vote.status === VoteStatus.open )
          const localVote = state.votes.find( vote => vote.id === liveVote?.voteId )
          const statusChanged = liveVote && localVote?.status !== VoteStatus.open

          // currently viewed vote goes live
          if((state.mode === VotesMode.DETAILS || state.mode === VotesMode.EDIT) &&
            parseInt( liveVote?.voteId ) === state?.selectedVoteId &&
            statusChanged
          )  state.mode = VotesMode.RESULTS

          //currently viewed vote has been deleted
          else if (
              state.selectedVoteId &&
              !payload?.data?.list.some( vote => parseInt(vote.voteId) === state.selectedVoteId)
            ){

            state.selectedVoteId = undefined
            state.mode = VotesMode.LIST

          }

          state.votes = payload.data.list.map(vote => {

            const { voteId, ...voteWithoutVoteId } = vote
            return {
              ...voteWithoutVoteId,
              id : parseInt(voteId)
            }

          })

        }

        else{

          if ( state.selectedVoteId && (state.mode === VotesMode.DETAILS || state.mode === VotesMode.EDIT) ) {

            state.selectedVoteId = undefined
            state.mode = VotesMode.LIST

          }

        }

      }
      else {

        state.votes = payload?.data?.votes ?? []

        if (state.selectedVoteId && !state.votes.some( vote => vote.id === state.selectedVoteId)) {

          state.selectedVoteId = undefined
          state.mode = VotesMode.LIST

        }

      }

    },
    setWebcastStatus( state, { payload }) {
      state.webcastStatus = payload
    },
    clearCountdown( state ){
      state.countdownStart = undefined
    },
    clearPendingChanges( state ){

      state.pendingChanges = []
    }
  },
  extraReducers: {
    [createVote.fulfilled.type] (state,{ payload }) {

      if (payload) state.votes.push(payload)
      if (payload?.id) state.pendingChanges.push(payload.id)
      state.mode = VotesMode.LIST

    },
    [createVote.rejected.type] (state) {
      state.mode = VotesMode.ERROR
    },
    [deleteVote.fulfilled.type] (state,{ payload }) {

      state.votes = state.votes.filter( vote => vote.id !== payload.id )
      if (payload?.id) state.pendingChanges.push(payload.id)
      state.mode = VotesMode.LIST

    },
    [deleteVote.rejected.type] (state) {
      state.mode = VotesMode.ERROR
    },
    [editVote.fulfilled.type] (state,{ payload }) {

      state.votes = state.votes.map( vote => vote.id !== payload.id ? vote : { ...vote, ...payload })
      state.pendingChanges.push( payload.id )
      state.selectedVoteId = payload.id
      state.mode = VotesMode.DETAILS
    },
    [editVote.rejected.type] (state) {
      state.mode = VotesMode.ERROR
    },
    [startVote.fulfilled.type] (state, { payload }) {

      const isOpenVote = id => payload.openVoteId === id
      const isStartedVote = id => payload.voteId === id
      state.votes = state.votes.map( vote => {

        if( isStartedVote( vote.id ) ){
          return { ...vote, status: VoteStatus.open }
        }
        else if( isOpenVote( vote.id) ){
          return  { ...vote, status: VoteStatus.closed }
        }
        else return vote

      })

      state.selectedVoteId = payload.voteId
      state.mode = VotesMode.RESULTS
      state.pendingChanges.push( payload.voteId )
      if(payload.openVoteId) state.pendingChanges.push( payload.openVoteId )

      state.countdownStart = Date.now()
    },
    [startVote.rejected.type] (state) {
      state.mode = VotesMode.ERROR
    },
    [endVote.fulfilled.type] (state, { payload }) {

      if (typeof payload?.id === 'undefined') {

        state.selectedVoteId = undefined

      }
      else {

        state.votes = state.votes.map( vote => payload.id === vote.id ? { ...vote, status: VoteStatus.closed } : vote )
        state.selectedVoteId = payload?.id
        state.pendingChanges.push( payload.id )

      }

      state.mode = VotesMode.RESULTS

    },
    [endVote.rejected.type] (state) {
      state.mode = VotesMode.ERROR
    },
    [endAllVotes.fulfilled.type] (state, { payload }) {

      if (typeof payload?.id === 'undefined') return
      state.votes = state.votes.map( vote => payload.id === vote.id ? { ...vote, status: VoteStatus.closed } : vote )
      state.pendingChanges.push( payload.id )

    },
  }
})


export const getUpcomingChanges = response => response.data.total ?
    response.data.votes.map( vote => ({id: vote.id, lastUpdated: vote.lastUpdated}))
    : []
export const getLiveChanges = response => response.data.list?.length ?
    response.data.list.map( vote => ({id: parseInt( vote.voteId ), lastUpdated: vote.lastUpdated}))
    : []

export const fetchVotes = (roomname: string) => async (dispatch, getState: () => State) => {

  const pendingChanges = getState().votes.pendingChanges
  const votes = getState().votes.votes
  const webcastStatus = selectWebcastStatusForVotes( getState() )
  const isLive = webcastStatus === WebcastStatusForVotes.LIVE

  let votesResponse

  if( pendingChanges.length ){

    dispatch( votesSlice.actions.clearPendingChanges() )
    return
  }

  try {

      votesResponse = isLive ? await apiGateway.getVotesSummaries(roomname) : await apiGateway.getVotes(roomname)
  }
  catch (e) {

    // TODO log failure of backend (once we have a backend)
    return
  }

  const localChangeStatus = votes.map( vote => ({id: vote.id, lastUpdated: vote.lastUpdated}))
  const remoteChangeStatus = isLive ? getLiveChanges( votesResponse ) : getUpcomingChanges( votesResponse )
  const changedVotes = xorWith( localChangeStatus, remoteChangeStatus, isEqual )

  if(changedVotes.length ) dispatch(votesSlice.actions.setVotes({ data: votesResponse.data, isLive: isLive } ))
  if(votesResponse.data.webcastStatus && votesResponse.data.webcastStatus !== webcastStatus) dispatch(
    votesSlice.actions.setWebcastStatus(votesResponse.data.webcastStatus)
  )

}

export default votesSlice
